import {
    CellClassParams,
    CellKeyDownEvent,
    CellValueChangedEvent,
    ColDef,
    ICellEditorParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useState } from 'react';
import { aggridHeaderClass, SmallTitleWithSubSpan } from 'src/components/common/common';
import { GridCalendar } from 'src/components/common/form/GridCalendar';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useUiuxStore from 'src/store/useUiuxStore';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import {
    CreateCellClickHandler, CustomCellClickAction, createCalendarCellClickHandler,
    createCellCssClassRules, createGridDropDownCommitAfter, GridCellConfig,
    RemoveRowHandler, useRowSelectionHandler,
} from 'src/utils/grid/GridHandlerContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';
import { showWarning } from 'src/utils/toast/toastUtil';
import { HRM1060_R01Dto } from './Dto_HRM1060_R01';
import { useHRM1060_R01Store } from './Store_HRM1060_R01';
import {
    CERT_STS_CANCEL,
    CERT_STS_ISSUED,
    headerNames_HRM1060_R01,
    isHRM1060_R01Printed,
    UNPRINTED_DATE_PREFIX,
    } from './Util_HRM1060_R01';

/**
 * 이 그리드의 사용자 컬럼 배치를 저장하는 키이다.
 * 사원 컬럼을 사원번호·사원명으로 나누면서 v2로 바꿨다. 공통 GridCustomColumnDefs는 저장된 배치에 있는 컬럼만 그리므로,
 * 예전 키를 그대로 쓰면 이전에 배치를 저장한 사용자에게 새 사원명 컬럼이 보이지 않는다.
 */
const COLUMN_STATE_KEY = 'HRM1060_R01-grid-column-state-v2';
/** Enter·Tab 이동에 쓰는 편집 컬럼 순서이다. */
const EDITABLE_COLUMNS = [
    'issueDt', 'empNo', 'certType', 'purpose', 'submitTo', 'copies', 'issueSts',
];
/** 날짜와 사원 검색을 제외하고 클릭 시 직접 편집할 컬럼이다. */
const DIRECT_EDIT_COLUMNS = ['certType', 'purpose', 'submitTo', 'copies', 'issueSts'];

/** 그리드 참조와 등록 함수를 상위 화면에서 전달한다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1060_R01Dto> | null>; // 편집 확정에 쓸 그리드 ref
    defaultHRM1060_R01Row: HRM1060_R01Dto; // 신규 행 기본값
    handleInsertClick: (
        event: React.MouseEvent,
        rowIndex?: number,
        setClickable?: (clickable: boolean) => void,
    ) => void; // 행 삽입 함수
}

/** 증명서 발급대장을 편집 그리드와 하단 건수 행으로 표시한다. @param props 참조와 등록 함수 @returns 대장 그리드 */
export const Table_HRM1060_R01 = ({ gridRef, defaultHRM1060_R01Row, handleInsertClick }: Props) => {
    const {
        HRM1060_R01List, setHRM1060_R01List, removeHRM1060_R01List, updateHRM1060_R01List,
        selectedHRM1060_R01List, addSelectedHRM1060_R01List, removeSelectedHRM1060_R01List,
        initSelectedHRM1060_R01List, allSelectedHRM1060_R01List,
        deletedHRM1060_R01List, addDeletedHRM1060_R01List, originalHRM1060_R01List,
        columnState, setColumnState, scrollTop, setScrollTop,
    } = useHRM1060_R01Store(); // 대장과 선택·삭제·그리드 상태
    const { certTypeList, certStsList } = useCommonCodeStore(); // 공통 증명서·상태 코드
    const { setCodeSearchModalProps } = useUiuxStore(); // 사원 검색 팝업
    const [clickable, setClickable] = useState(true); // true이면 행 클릭을 선택으로 처리
    const [calendar, setCalendar] = useState({
        pickerVisible: false, pickerPosition: { top: 0, left: 0 },
        targetRowIndex: null as number | null, targetField: null as string | null,
        targetDate: undefined as Date | undefined,
    }); // 발급일자 셀의 공통 달력 상태

    /** 달력 상태 중 전달된 필드만 갱신한다. @param patch 변경할 달력 필드 */
    const setCalendarState = useCallback((patch: Partial<typeof calendar>) => {
        setCalendar(state => ({ ...state, ...patch }));
    }, []);

    /** 그리드 공통 함수가 변경한 행을 Store에 보관한다. @param index valueIndex @param row 변경 행 */
    const updateRow = useCallback((index: number, row: HRM1060_R01Dto) => {
        updateHRM1060_R01List(index, row);
    }, [updateHRM1060_R01List]);

    const {
        getRowId, createRowClassRules, createCellClassRules, paddedGridData, renderIndexCell,
    } = useGridSettingContent<HRM1060_R01Dto>({
        gridRef, key: 'valueIndex', gridData: HRM1060_R01List,
        defaultGridData: defaultHRM1060_R01Row, selectedRow: selectedHRM1060_R01List,
        originalGridData: originalHRM1060_R01List, updateRow,
    }); // 공통 행 ID·행 번호·선택 스타일

    /** 출력한 행은 감사 기록 보존을 위해 업무 칸 전체를 잠근다. @param row 대장 행 @returns true이면 일반 칸 편집 가능 */
    const canEdit = useCallback((row: HRM1060_R01Dto | undefined) =>
        !!row && !isHRM1060_R01Printed(row), []);

    /** 서버에서 조회한 원래 상태를 찾는다. 신규 행은 원본이 없어 현재 값을 쓴다. @param row 대장 행 @returns 저장된 상태 코드 */
    const getOriginalIssueSts = useCallback((row: HRM1060_R01Dto) => {
        if (row.inserted || !row.certNo) return row.issueSts;
        // ??: 원본 목록에서 같은 발급번호를 찾지 못하면 현재 값을 원래 상태로 본다.
        return originalHRM1060_R01List.find(origin => origin.certNo === row.certNo)?.issueSts ?? row.issueSts;
    }, [originalHRM1060_R01List]);

    /**
     * 상태 칸은 출력 후에는 취소만 허용하고, 출력 후 취소가 저장된 행은 잠근다.
     * 저장 전 화면 값이 아니라 원래 상태로 판단해, 실수로 고른 취소를 저장 전에 되돌릴 수 있게 한다.
     * @param row 대장 행 @returns true이면 상태 편집 가능
     */
    const canEditStatus = useCallback((row: HRM1060_R01Dto | undefined) =>
        !!row && (!isHRM1060_R01Printed(row) || getOriginalIssueSts(row) !== CERT_STS_CANCEL), [getOriginalIssueSts]);

    /** 출력한 행에서 고를 수 있는 상태인지 확인한다. 취소 또는 원래 상태(되돌리기)만 허용한다. @param row 대장 행 @param value 새 상태 @returns true이면 변경 가능 */
    const canChangeStatus = useCallback((row: HRM1060_R01Dto, value: string) =>
        !isHRM1060_R01Printed(row) || value === CERT_STS_CANCEL || value === getOriginalIssueSts(row), [getOriginalIssueSts]);

    /**
     * 상태 변경 후의 수정 여부를 계산한다. 출력한 행은 상태 외에 바꿀 수 없으므로 원래 상태로 되돌리면 수정 없음으로 본다.
     * 원래 상태(발급 20)로 되돌린 행을 그대로 보내면 서버가 「발급 상태는 출력할 때만 바뀝니다.」로 거부하기 때문이다.
     * @param row 대장 행 @param value 새 상태 @returns true이면 저장 대상
     */
    const getStatusModified = useCallback((row: HRM1060_R01Dto, value: string) => {
        if (row.inserted) return false;
        return isHRM1060_R01Printed(row) ? value !== getOriginalIssueSts(row) : true;
    }, [getOriginalIssueSts]);

    const handleStatusChange = useMemo(() => CommonCodeColumn.change(updateRow, {
        canChange: ({ data, newValue }) => canChangeStatus(data, newValue),
        getPatch: ({ data, newValue }) => ({ isModified: getStatusModified(data, newValue) }),
    }), [updateRow, canChangeStatus, getStatusModified]); // Store 변경 함수와 원래 상태 판단이 바뀔 때 두 편집 경로의 콜백을 함께 갱신한다

    const cssCellConfig = useMemo<GridCellConfig>(() => ({
        disabledCells: ['certNo', 'empNm', 'printCnt', 'lastPrintDt'], // 사원명은 사원번호 팝업으로만 바뀌므로 회색으로 표시
        insertedDisabledCells: [], commonDisabledCells: [],
        pointerCells: EDITABLE_COLUMNS, insertedPointerCells: [], commonPointerCells: [],
        alignLeftCells: ['purpose', 'submitTo'],
    }), []); // 컬럼별 공통 스타일 대상은 고정 목록으로 유지한다

    const {
        disabledInsertedColumns, disabledColumns,
        pointerInsertedColumns, pointerColumns, alignLeftColumns,
    } = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]); // 신규·수정·선택 행의 공통 셀 스타일

    const cellCssDefaultColDef = useMemo(() => {
        const commonCellClassRules = createCellClassRules<HRM1060_R01Dto>({
            idKey: 'valueIndex', selectedList: selectedHRM1060_R01List.map(Number),
            deletedList: deletedHRM1060_R01List.map(Number), disabledInsertedColumns, disabledColumns,
            pointerInsertedColumns, pointerColumns, alignLeftColumns,
        }); // 기존 선택·삭제·신규·고정 비활성 셀의 공통 규칙
        return {
            ...defaultColDef, // 그리드 기본 셀 설정
            cellClassRules: {
                ...commonCellClassRules, // 공통 셀 상태 색상과 정렬을 유지한다
                'disabled-cell': (params: CellClassParams<HRM1060_R01Dto>) => {
                    if (commonCellClassRules['disabled-cell'](params)) return true;
                    if (!params.data || params.node.rowPinned) return false;
                    const field = params.colDef.field; // 편집 가능 여부를 검사할 업무 컬럼
                    const isStatusColumn = field === 'issueSts'; // true이면 상태 칸의 별도 잠금 규칙을 적용
                    if (isStatusColumn) return !canEditStatus(params.data);
                    const isBusinessColumn = EDITABLE_COLUMNS.includes(field ?? ''); // true이면 출력 후 잠기는 업무 칸
                    return isBusinessColumn && !canEdit(params.data);
                }, // 출력 후 잠긴 칸을 발급번호와 같은 회색으로 표시한다
            },
        };
    }, [createCellClassRules, selectedHRM1060_R01List, deletedHRM1060_R01List,
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns,
        canEdit, canEditStatus]); // 선택·삭제 목록과 편집 가능 조건이 바뀌면 셀 색상 규칙을 갱신한다

    /** 사원 셀에서 공통 검색 팝업을 열고 선택 결과를 해당 행에 기록한다. @param row 선택할 행 */
    const openEmployeeSearch = useCallback((row: HRM1060_R01Dto) => {
        if (!canEdit(row)) return;
        setCodeSearchModalProps({
            category: '사원', search: row.empNo, formName: 'HRM1060_R01', isInfo: true,
            handleFoucsId: 'HRM1060_R01-employee-grid',
            onClear: () => updateRow(row.valueIndex, {
                ...row, empNo: '',
                empNm: '',
                isModified: row.inserted ? false : true,
            }),
            onConfirm: employee => updateRow(row.valueIndex, {
                ...row, empNo: employee?.empNo || '',
                empNm: employee?.empNm || '',
                isModified: row.inserted ? false : true,
            }),
            processType_focus: 'normal',
        });
    }, [canEdit, setCodeSearchModalProps, updateRow]);

    /** 선택한 날짜를 발급일자에 반영한다. @param date 달력 날짜 @param rowIndex 표시 순서 @param field 날짜 필드 */
    const handleCalendarChange = useCallback((date: Date | undefined, rowIndex: number | null, field: string | null) => {
        if (rowIndex === null || field !== 'issueDt' || !date) return;

        const valueIndex = gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data?.valueIndex; // 정렬 뒤 실제 행 식별값
        const row = useHRM1060_R01Store.getState().HRM1060_R01List.find(item => item.valueIndex === valueIndex); // 수정 대상

        if (!row || !canEdit(row)) return;
        const issueDt = formatDate(date); // 서버 계약 날짜 문자열
        if (row.issueDt !== issueDt) updateRow(row.valueIndex, {
            ...row, issueDt, isModified: row.inserted ? false : true,
        });
    }, [gridRef, canEdit, updateRow]);

    /** 달력을 열 때 셀의 현재 날짜를 표시한다. @param rowIndex 표시 순서 @param field 날짜 필드 */
    const handleCalendarLoad = useCallback((rowIndex: number, field: string) => {
        const row = gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data; // 화면 표시 순서의 행

        setCalendarState({ targetDate: row?.issueDt ? new Date(row.issueDt) : new Date() });
    }, [gridRef, setCalendarState]);

    const handleDropDownCommit = useMemo(
        () => createGridDropDownCommitAfter<HRM1060_R01Dto>(EDITABLE_COLUMNS), [],
    ); // Enter·Tab 후 다음 편집 셀 이동

    /** 행 번호 칸의 -는 출력 기록이 있는 행에 삭제 표시를 하지 않는다. @param row 클릭한 행 */
    const handleRemoveRow = useCallback((row: HRM1060_R01Dto) => {
        setClickable(false);
        if (isHRM1060_R01Printed(row)) {
            showWarning('출력한 증명서는 취소로 처리하세요.');
            return;
        }
        RemoveRowHandler(gridRef, HRM1060_R01List, row, setClickable,
            removeHRM1060_R01List, ids => addDeletedHRM1060_R01List(ids.map(String)));
    }, [gridRef, HRM1060_R01List, removeHRM1060_R01List, addDeletedHRM1060_R01List]);

    /** 행별 index와 업무 컬럼을 정의한다. */
    const baseColumnDefs = useMemo<ColDef<HRM1060_R01Dto>[]>(() => [
        ...getDefaultRowIndexColumnConfig((params: HRM1060_R01Dto) => renderIndexCell(
            params,
            (inner: HRM1060_R01Dto) => getRowStyleFlags(inner, {
                deletedList: deletedHRM1060_R01List.map(Number) as unknown as string[],
                selectedList: selectedHRM1060_R01List.map(Number) as unknown as string[],
                dataList: HRM1060_R01List,
            }),
            (rowIndex: number, event: React.MouseEvent) => handleInsertClick(event, rowIndex, setClickable),
            handleRemoveRow,
        )), // 공통 index 컬럼은 상태와 +/- 버튼을 표시한다
        { headerName: '발급번호', colId: 'certNo', field: 'certNo', width: 104,
            valueFormatter: params => params.value ?? '', },
        { headerName: '발급일자', colId: 'issueDt', field: 'issueDt', width: 132,
            editable: params => !params.node.rowPinned && canEdit(params.data) },
        // 사원번호 셀은 직접 입력하지 않고 더블클릭·Enter로 사원 검색 팝업을 열어 채운다.
        { headerName: '사원번호', colId: 'empNo', field: 'empNo', width: 100,
            editable: false },
        // 사원명은 팝업에서 사원번호와 함께 채워지는 표시 전용 값이라 편집하지 않는다.
        { headerName: '사원명', colId: 'empNm', field: 'empNm', width: 88,
            editable: false },
        { headerName: '증명서', colId: 'certType', field: 'certType', width: 100,
            ...CommonCodeColumn.options<HRM1060_R01Dto>(certTypeList,
                params => !params.node.rowPinned && canEdit(params.data),
                CommonCodeColumn.change(updateRow), handleDropDownCommit) },
        { headerName: '용도', colId: 'purpose', field: 'purpose', width: 120,
            editable: params => !params.node.rowPinned && canEdit(params.data),
            cellEditorParams: { maxLength: 100 } },
        { headerName: '제출처', colId: 'submitTo', field: 'submitTo', width: 120,
            editable: params => !params.node.rowPinned && canEdit(params.data),
            cellEditorParams: { maxLength: 100 } },
        { headerName: '매수', colId: 'copies', field: 'copies', width: 52,
            editable: params => !params.node.rowPinned && canEdit(params.data),
            cellEditor: 'agNumberCellEditor', valueParser: params => Number(params.newValue),
            cellStyle: { textAlign: 'right' } },
        { headerName: '상태', colId: 'issueSts', field: 'issueSts', width: 76,
            // 표시명은 발급(20)을 포함한 전체 코드로 찾고, 선택지는 아래 편집기에서 제한한다.
            ...CommonCodeColumn.options<HRM1060_R01Dto>(
                certStsList,
                params => !params.node.rowPinned && canEditStatus(params.data),
                handleStatusChange, handleDropDownCommit),

            // 출력 전에는 신청·취소, 출력 후에는 취소와 원래 상태(되돌리기용)만 보여 준다.
            // 발급(20)은 출력 API에서만 설정하므로 출력 전 행의 선택지에는 넣지 않는다.
            cellEditorParams: (params: ICellEditorParams<HRM1060_R01Dto>) => ({
                optionList: certStsList
                    .filter(code => {
                        if (!params.data || !isHRM1060_R01Printed(params.data)) return code.minorCd !== CERT_STS_ISSUED;
                        return code.minorCd === CERT_STS_CANCEL || code.minorCd === getOriginalIssueSts(params.data);
                    })
                    .map(code => ({ value: code.minorCd, label: code.codeNm })),
                onChange: handleStatusChange,
                onCommitAfter: handleDropDownCommit,
            }) },
        { headerName: '출력', colId: 'printCnt', field: 'printCnt', width: 52,
            cellStyle: { textAlign: 'right' } },
        { headerName: '최종 출력', colId: 'lastPrintDt', field: 'lastPrintDt', width: 150,
            valueFormatter: params => {
                const value = String(params.value ?? ''); // 서버의 ISO 시각 문자열
                return value.startsWith(UNPRINTED_DATE_PREFIX) ? '' : value.replace('T', ' ').slice(0, 19);
            } },
    ], [renderIndexCell, deletedHRM1060_R01List, selectedHRM1060_R01List, HRM1060_R01List,
        handleInsertClick, handleRemoveRow, canEdit, certTypeList, updateRow, handleDropDownCommit,
        canEditStatus, certStsList, handleStatusChange, getOriginalIssueSts]);

    const defaultColumnState = useMemo(() =>
        buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]); // 최초 컬럼 배치

    const columnDefs = useMemo(() => {
        if (columnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<HRM1060_R01Dto>({
            baseColumnDefs, columnState, headerNames: headerNames_HRM1060_R01,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, columnState]); // 저장 배치가 있으면 공통 함수로 복원한다

    const selectedIdsForHook = useMemo(
        () => selectedHRM1060_R01List.map(Number) as unknown as string[],
        [selectedHRM1060_R01List],
    ); // 공통 훅은 숫자 valueIndex를 비교하므로 선택값을 변환

    const deletedIdsForHook = useMemo(
        () => deletedHRM1060_R01List.map(Number) as unknown as string[],
        [deletedHRM1060_R01List],
    ); // 공통 훅은 숫자 valueIndex를 비교하므로 삭제값을 변환


    const handleRowClick = useRowSelectionHandler<HRM1060_R01Dto>({
        dataList: HRM1060_R01List, idKey: 'valueIndex',
        selectedList: selectedIdsForHook, deletedList: deletedIdsForHook,
        addSelected: addSelectedHRM1060_R01List, removeSelected: removeSelectedHRM1060_R01List,
        clickable, setClickable,
    }); // 하단 건수 행과 +/- 직후 클릭을 무시하는 공통 행 선택

    /** 셀 직접 편집 결과를 Store에 반영한다. @param event 변경된 셀 이벤트 */
    const handleCellChange = useCallback((event: CellValueChangedEvent<HRM1060_R01Dto>) => {
        if (event.node.rowPinned || !event.data) return;
        const row = useHRM1060_R01Store.getState().HRM1060_R01List
            .find(item => item.valueIndex === event.data?.valueIndex); // 셀 변경 대상

        if (!row || (event.colDef.field !== 'issueSts' && !canEdit(row))) return;
        const isStatusField = event.colDef.field === 'issueSts'; // true이면 상태 칸 변경

        if (isStatusField && (!canEditStatus(row) || !canChangeStatus(row, String(event.newValue)))) return;
        const field = event.colDef.field as keyof HRM1060_R01Dto; // 변경된 업무 컬럼

        // AG Grid가 data를 먼저 바꿀 수 있으므로 이전 셀 값으로 실제 변경 여부를 판단한다.
        if (!field || event.oldValue === event.newValue) return;
        updateRow(row.valueIndex, {
            ...row, [field]: event.newValue,

            // 삼항: 상태 칸은 원래 상태로 되돌렸는지까지 보고, 나머지 칸은 기존 행이면 수정으로 표시한다.
            isModified: isStatusField ? getStatusModified(row, String(event.newValue)) : !row.inserted,
        });
    }, [canEdit, canEditStatus, canChangeStatus, getStatusModified, updateRow]);

    const cellClickActions = useMemo<CustomCellClickAction<HRM1060_R01Dto>[]>(() => [
        { validate: (field, row) => field === 'issueDt' && canEdit(row),
            action: createCalendarCellClickHandler<HRM1060_R01Dto>('issueDt', calendar, setCalendarState) },
        { validate: (field, row) => DIRECT_EDIT_COLUMNS.includes(field)
                && (field === 'issueSts' ? canEditStatus(row) : canEdit(row)),
            action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }) },
    ], [canEdit, canEditStatus, calendar, setCalendarState]); // 날짜는 달력, 나머지는 셀 편집

    const handleCellClick = CreateCellClickHandler(HRM1060_R01List, 'valueIndex', cellClickActions); // 공통 클릭 분배

    const cellEventHandlers = useDetailTableEvents({
        detailList: HRM1060_R01List, editableColumns: EDITABLE_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    }); // 공통 방향키 이동

    /** 사원 셀에서 Enter를 누르면 검색 팝업을 연다. @param event 키 이벤트 */
    const handleCellKeyDown = useCallback((event: CellKeyDownEvent<HRM1060_R01Dto>) => {
        cellEventHandlers.handleCellKeyDown(event);
        if (event.colDef.field === 'empNo' && event.data && (event.event as KeyboardEvent).key === 'Enter') {
            openEmployeeSearch(event.data);
        }
    }, [cellEventHandlers, openEmployeeSearch]);

    const pinnedBottomRow = useMemo(() => [{
        certNo: HRM1060_R01List.length.toLocaleString() + '건',
    }], [HRM1060_R01List]); // 표시한 실제 행만 세며 index 하단 칸은 비운다

    return (
        <GridContextWrapper<HRM1060_R01Dto>
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            initSelected={initSelectedHRM1060_R01List}
            allSelected={allSelectedHRM1060_R01List}
            defaultRowData={defaultHRM1060_R01Row}
            baseColumnDefs={columnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setColumnState}
            localStorageKey={COLUMN_STATE_KEY}
        >
            {/* 발급대장 영역 제목은 공통 파란 막대를 사용한다. */}
            <SmallTitleWithSubSpan title="증명서 발급대장" />

            {/* 편집·선택·건수 행을 포함한 실제 대장 그리드이다. */}
            <Grid_Excel<HRM1060_R01Dto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<HRM1060_R01Dto>({
                    idKey: 'valueIndex',
                    selectedList: selectedHRM1060_R01List.map(Number),
                    deletedList: deletedHRM1060_R01List.map(Number),
                })}
                onRowClicked={handleRowClick}
                onCellClicked={handleCellClick}
                onCellDoubleClicked={event => {
                    if (event.colDef.field !== 'empNo' || !event.data || event.node.rowPinned) return;
                    openEmployeeSearch(event.data);
                }}
                onCellKeyDown={handleCellKeyDown}
                onCellValueChanged={handleCellChange}
                pinnedBottomRowData={pinnedBottomRow}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={scrollTop}
                setScrollTop={setScrollTop}
            />

            {/* 발급일자 셀의 공통 날짜 달력이다. */}
            <GridCalendar
                rowIndex={calendar.targetRowIndex ?? 0}
                targetField={calendar.targetField ?? ''}
                visible={calendar.pickerVisible}
                setVisible={visible => setCalendarState({ pickerVisible: visible })}
                value={calendar.targetDate}
                pickerPosition={calendar.pickerPosition}
                setPickerPosition={pickerPosition => setCalendarState({ pickerPosition })}
                onClear={() => setCalendarState({ targetDate: undefined })}
                onChange={handleCalendarChange}
                onClickLoad={handleCalendarLoad}
            />
        </GridContextWrapper>
    );
};
